import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import dayjs from '~/timeInit';

import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';

import { getRegionByCode, getRegions } from '@luxuryescapes/lib-regions';

import UserBreadcrumbs from '~/components/Common/Elements/UserBreadcrumbs';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Spinner from '~/components/Common/Spinner';
import AtolCertificateHtmlViewer from '~/components/Users/Orders/Atol/AtolCertificateHtmlViewer';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import {
  AtolOfflineFlightsPayload,
  AtolPreviewResponse,
  previewAtolOfflineFlightsCertificate,
  sendAtolOfflineFlightsCertificate,
} from '~/services/TravelComplianceService';

import ApiError from '~/types/ApiError';

const DEFAULT_ATOL_REGION_CODE = 'GB';
const DEFAULT_ATOL_CURRENCY_CODE = 'GBP';

export type AtolFormData = {
  pnrs: Array<string>;
  regionCode: string;
  currencyCode: string;
};

const DEFAULT_ATOL_FORM: AtolFormData = {
  pnrs: [],
  regionCode: DEFAULT_ATOL_REGION_CODE,
  currencyCode: DEFAULT_ATOL_CURRENCY_CODE,
};

/** Decode HTML entities from an escaped HTML string */
function decodeHtmlEntities(html: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = html;
  return textarea.value;
}

/** Extract error message from API error response */
function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
      const errorMessages = error.errors
        .map((e: unknown) => {
          if (typeof e === 'string') {
            return e;
          }
          if (typeof e === 'object' && e !== null && 'message' in e) {
            return (e as { message: string }).message;
          }
          return null;
        })
        .filter(Boolean);
      if (errorMessages.length > 0) {
        return errorMessages.join('; ');
      }
    }
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

interface PreviewData {
  customerEmail: string;
  uniqueReferenceNumber: string;
  totalProtectedCost: number;
  certificateHtml: string;
  travellersCount: number;
  previewedAt: Date;
}

function AtolPage() {
  const { id_user: customerId } = useParams<{ id_user: string }>();
  const { tenant } = useCurrentTenant();
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const [atolData, setAtolData] = useState<AtolFormData>(DEFAULT_ATOL_FORM);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [sendStatus, setSendStatus] = useState<{ success: boolean; message: string } | null>(null);

  const regions = useMemo(() => getRegions(tenant.brand), [tenant.brand]);

  const currencyOptions = useMemo(() => [...new Set(regions.map((r) => r.currencyCode).filter(Boolean))], [regions]);

  const handleRegionChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const regionCode = event.target.value;
      const region = getRegionByCode(regionCode, tenant.brand);
      if (!region) {
        return;
      }
      setAtolData((prev) => ({
        ...prev,
        regionCode: region.code,
        currencyCode: region.currencyCode,
      }));
      setPreviewData(null);
    },
    [tenant.brand],
  );

  const handleCurrencyChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setAtolData((prev) => ({ ...prev, currencyCode: event.target.value }));
    setPreviewData(null);
  }, []);

  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Build the payload for API calls
  const buildPayload = useCallback((): AtolOfflineFlightsPayload => {
    return {
      pnrs: atolData.pnrs,
      regionCode: atolData.regionCode,
      currencyCode: atolData.currencyCode,
      customerId,
      brand: tenant.brand,
    };
  }, [atolData.pnrs, atolData.regionCode, atolData.currencyCode, customerId, tenant.brand]);

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: (payload: AtolOfflineFlightsPayload) => previewAtolOfflineFlightsCertificate(payload),
    onMutate: () => {
      // Clear previous state before starting
      setPreviewError(null);
      setPreviewData(null);
      setSendStatus(null);
    },
    onSuccess: (response: AtolPreviewResponse) => {
      if (!isMountedRef.current) {
        return;
      }
      setPreviewData({
        customerEmail: response.result.customerEmail,
        uniqueReferenceNumber: response.result.uniqueReferenceNumber,
        totalProtectedCost: response.result.totalProtectedCost,
        certificateHtml: decodeHtmlEntities(response.result.certificateHtml),
        travellersCount: response.result.travellersCount,
        previewedAt: new Date(),
      });
    },
    onError: (error: unknown) => {
      if (!isMountedRef.current) {
        return;
      }
      setPreviewError(getErrorMessage(error));
    },
  });

  // Send mutation
  const sendMutation = useMutation({
    mutationFn: (payload: AtolOfflineFlightsPayload) => sendAtolOfflineFlightsCertificate(payload),
    onMutate: () => {
      setSendStatus(null);
    },
    onSuccess: (response) => {
      // enqueueSnackbar is safe to call after unmount
      enqueueSnackbar(`ATOL certificate sent successfully! Reference: ${response.result.uniqueReferenceNumber}`, {
        variant: 'success',
      });
      if (!isMountedRef.current) {
        return;
      }
      setSendStatus({
        success: true,
        message: `ATOL certificate sent successfully. Reference: ${response.result.uniqueReferenceNumber}`,
      });
      // Reset form after successful send
      setAtolData(DEFAULT_ATOL_FORM);
      setPreviewData(null);
    },
    onError: (error: unknown) => {
      const errorMessage = getErrorMessage(error);
      enqueueSnackbar(`Failed to send ATOL certificate: ${errorMessage}`, { variant: 'error' });
      if (!isMountedRef.current) {
        return;
      }
      setSendStatus({
        success: false,
        message: errorMessage,
      });
    },
  });

  const handleFormChange = useCallback((name: keyof AtolFormData, value: Array<string>) => {
    setAtolData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handlePreview = useCallback(() => {
    if (atolData.pnrs.length === 0) {
      setPreviewError('Please enter at least one PNR code');
      return;
    }
    previewMutation.mutate(buildPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable in TanStack Query
  }, [atolData, buildPayload, previewMutation.mutate]);

  const handleSend = useCallback(async () => {
    if (atolData.pnrs.length === 0) {
      enqueueSnackbar('Please enter at least one PNR code', { variant: 'warning' });
      return;
    }

    // Build confirmation message
    let confirmDescription;
    if (previewData) {
      confirmDescription =
        `You have previewed the certificate on ${dayjs(previewData.previewedAt).format('DD MMM YYYY HH:mm:ss')}. ` +
        `Are you sure all information is correct and you want to send the ATOL certificate to ${previewData.customerEmail}?`;
    } else {
      confirmDescription =
        'Warning: You have not previewed the certificate in this session. ' +
        'Are you sure you want to send the ATOL certificate without previewing it first?';
    }

    const confirmed = await showConfirmDialog({
      title: 'Confirm Send ATOL Certificate',
      description: confirmDescription,
    });

    if (!confirmed) {
      return;
    }

    sendMutation.mutate(buildPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mutate is stable in TanStack Query
  }, [atolData, buildPayload, enqueueSnackbar, previewData, sendMutation.mutate, showConfirmDialog]);

  const handleFormSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleSend();
    },
    [handleSend],
  );

  const isPreviewLoading = previewMutation.isPending;
  const isSendLoading = sendMutation.isPending;
  const isAnyLoading = isPreviewLoading || isSendLoading;

  return (
    <Container maxWidth="xl">
      <UserBreadcrumbs userId={customerId} currentPage="ATOL" />
      <Card>
        <CardHeader title="Send ATOL Certificate (Offline Flights)" />
        <CardContent>
          <form onSubmit={handleFormSubmit}>
            <Grid container spacing={3}>
              <Grid size={12}>
                <Typography variant="body2" color="text.secondary">
                  Customer ID: {customerId} | Brand: {tenant.brand}
                </Typography>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Region"
                  value={atolData.regionCode}
                  onChange={handleRegionChange}
                  fullWidth
                  disabled={isAnyLoading}
                >
                  {regions.map((region) => (
                    <MenuItem key={region.code} value={region.code}>
                      {`${region.name} (${region.currencyCode})`}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  select
                  label="Currency"
                  value={atolData.currencyCode}
                  onChange={handleCurrencyChange}
                  fullWidth
                  disabled={isAnyLoading}
                  helperText="Auto-set when region changes, but can be overridden"
                >
                  {currencyOptions.map((code) => (
                    <MenuItem key={code} value={code}>
                      {code}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={atolData.pnrs}
                  onChange={(_, newValue) => {
                    // Remove empty strings and trim whitespace
                    const sanitized = newValue.map((pnr) => pnr.trim()).filter((pnr) => pnr.length > 0);
                    handleFormChange('pnrs', sanitized);
                  }}
                  disabled={isAnyLoading}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index });
                      return <Chip variant="outlined" label={option} key={key} {...tagProps} />;
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="PNR Codes"
                      placeholder="Type a PNR code and press Enter"
                      helperText="Enter the PNR codes for the flights to be included in the ATOL certificate. The first PNR code determines the customer email address and passengers covered by the certificate."
                      required={atolData.pnrs.length === 0}
                    />
                  )}
                />
              </Grid>
              <Grid size={12}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    type="button"
                    size="large"
                    variant="outlined"
                    color="primary"
                    disabled={isAnyLoading || atolData.pnrs.length === 0}
                    onClick={handlePreview}
                    endIcon={isPreviewLoading ? <Spinner size={24} color="grey" /> : null}
                  >
                    Preview
                  </Button>
                  <Button
                    type="submit"
                    size="large"
                    variant="contained"
                    color="primary"
                    disabled={isAnyLoading || atolData.pnrs.length === 0}
                    endIcon={isSendLoading ? <Spinner size={24} color="grey" /> : null}
                  >
                    Send
                  </Button>
                </Stack>
              </Grid>

              {sendStatus && (
                <Grid size={12}>
                  <Alert severity={sendStatus.success ? 'success' : 'error'} onClose={() => setSendStatus(null)}>
                    {sendStatus.message}
                  </Alert>
                </Grid>
              )}
            </Grid>
          </form>
        </CardContent>
      </Card>

      {previewError && (
        <Box mt={3}>
          <Alert severity="error" onClose={() => setPreviewError(null)}>
            {previewError}
          </Alert>
        </Box>
      )}

      {isPreviewLoading && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 300,
                gap: 2,
              }}
            >
              <CircularProgress size={48} />
              <Typography variant="body1" color="text.secondary">
                Generating ATOL certificate preview...
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {previewData && !isPreviewLoading && (
        <Card sx={{ mt: 3 }}>
          <CardHeader
            title="ATOL Certificate Preview"
            subheader={`Previewed on: ${dayjs(previewData.previewedAt).format('DD MMM YYYY HH:mm:ss')}`}
          />
          <CardContent>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Customer Email
                  </Typography>
                  <Typography variant="body2">{previewData.customerEmail}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Reference Number
                  </Typography>
                  <Typography variant="body2">{previewData.uniqueReferenceNumber}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Total Protected Cost
                  </Typography>
                  <Typography variant="body2">
                    {atolData.currencyCode} {previewData.totalProtectedCost.toFixed(2)}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Typography variant="caption" color="text.secondary">
                    Travellers
                  </Typography>
                  <Typography variant="body2">{previewData.travellersCount}</Typography>
                </Grid>
              </Grid>
            </Box>

            <AtolCertificateHtmlViewer certificateHtml={previewData.certificateHtml} title="ATOL Certificate Preview" />
          </CardContent>
        </Card>
      )}
    </Container>
  );
}

export default AtolPage;
